import classNames from 'classnames';
import React, {useCallback, useState} from 'react';

import {
    BackendSourceOptions,
    DiscoveryItemClickEvent,
    DiscoveryItemImpressionEvent,
} from '@udemy/browse-event-tracking';
import {Tracker, TrackImpression, generateTrackingId} from '@udemy/event-tracking';
import {useMatchMedia} from '@udemy/hooks';
import {useFormatNumber, useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {Button, IconButton} from '@udemy/react-core-components';
import {
    CardMediaImage,
    CardProgress,
    CardTitle,
    CourseProductCard,
    Tag,
} from '@udemy/react-product-card-components';

import {LearningPathCourseCta} from './learning-path-course-cta.react-component';
import styles from './learning-path-course.module.less';

import type {CourseStatus} from '../learning-path-status';

const UI_REGION = 'bundle-unit';

type Step = {
    id?: string | number;
    courseTitle?: string;
    durationMinutes?: number;
    tags?: string[];
    ratingAverage?: number;
    ratingCount?: number;
    progressPercent?: number;
    thumbnailUrl?: string;
    cta?: {
        href?: string;
        certificateHref?: string;
        certificateReady?: boolean;
    };
    data?: {
        summary?: string;
    };
    details?: {
        title?: string;
        keyPoints?: string[];
    };
};

interface LearningPathCourseProps {
    course: Step;
    status: CourseStatus;
    courseNumber: number;
    totalCourses: number;
    isFirstIncompleteCourse: boolean;
    isEnrolled?: boolean;
    defaultExpanded?: boolean;
}

export function LearningPathCourse({
    course,
    status,
    courseNumber,
    totalCourses,
    isFirstIncompleteCourse,
    isEnrolled = true,
    defaultExpanded = false,
}: LearningPathCourseProps) {
    const {gettext, interpolate, ngettext, ninterpolate} = useI18n();
    const {formatNumber} = useFormatNumber();
    const isWiderThanMdMin = useMatchMedia('md-min');
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    const {
        courseTitle,
        thumbnailUrl,
        tags = [],
        ratingAverage,
        ratingCount,
        durationMinutes,
        data,
        cta,
        progressPercent,
        details,
    } = course;
    const normalizedProgressPercent = typeof progressPercent === 'number' ? progressPercent : 0;

    const ratingAverageText =
        typeof ratingAverage === 'number'
            ? ratingAverage.toLocaleString(undefined, {maximumFractionDigits: 1})
            : null;
    const hasRatingCount = typeof ratingCount === 'number';
    const ratingCountText = hasRatingCount ? formatNumber(ratingCount) : null;
    const ratingCountTag =
        ratingCountText && hasRatingCount
            ? interpolate(
                  ngettext('%(count)s rating', '%(count)s ratings', ratingCount),
                  {count: ratingCountText},
                  true,
              )
            : null;

    const trackerText = interpolate(
        gettext('Course %(courseNumber)s of %(totalCourses)s'),
        {
            courseNumber,
            totalCourses,
        },
        true,
    );
    const durationTag =
        typeof durationMinutes === 'number'
            ? durationMinutes < 60
                ? ninterpolate('%(minutes)s min', '%(minutes)s mins', durationMinutes, {
                      minutes: durationMinutes.toFixed(0),
                  })
                : ninterpolate(
                      '%(hours)s hour',
                      '%(hours)s hours',
                      Math.floor(durationMinutes / 60),
                      {
                          hours: Number((durationMinutes / 60).toFixed(1)),
                      },
                  )
            : null;

    const tagItems: Array<{key: string; node: React.ReactNode}> = [];
    const pushTag = (key: string, node: React.ReactNode) => tagItems.push({key, node});

    pushTag(`tracker-${courseNumber}-${totalCourses}`, trackerText);

    if (ratingAverageText) {
        pushTag(
            'rating-average',
            <span className={styles['rating-average']}>
                <Icon
                    glyph="rating-star"
                    size="xsmall"
                    label={false}
                    className={styles['rating-star']}
                />
                <span>{ratingAverageText}</span>
            </span>,
        );
    }

    if (ratingCountTag) {
        pushTag('rating-count', ratingCountTag);
    }

    tags.forEach((tag, idx) => pushTag(`tag-${idx}-${tag}`, tag));

    if (durationTag) {
        pushTag('duration', durationTag);
    }

    if (data?.summary) {
        pushTag('includes-summary', gettext('Includes summary'));
    }

    const courseId = Number(course.id ?? courseNumber);
    const trackingId = String(course.id ?? courseNumber);

    const trackImpression = useCallback(
        () =>
            Tracker.publishEvent(
                new DiscoveryItemImpressionEvent({
                    id: courseId,
                    type: 'course',
                    trackingId: generateTrackingId(),
                    serveTrackingId: trackingId,
                    backendSource: BackendSourceOptions.DISCOVERY,
                    position: courseNumber,
                    badgeFamilies: null,
                    relatedSourceId: null,
                    relatedSourceType: null,
                    uiRegion: UI_REGION,
                }),
            ),
        [courseId, trackingId, courseNumber],
    );

    const trackClick = useCallback(
        () =>
            Tracker.publishEvent(
                new DiscoveryItemClickEvent({
                    id: courseId,
                    type: 'course',
                    trackingId: generateTrackingId(),
                    serveTrackingId: trackingId,
                    backendSource: BackendSourceOptions.DISCOVERY,
                    badgeFamilies: null,
                    uiRegion: UI_REGION,
                }),
            ),
        [courseId, trackingId],
    );
    const handleMediaLinkClick = useCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            event.stopPropagation();
            trackClick();
        },
        [trackClick],
    );

    const handleCardRowClick = useCallback(() => {
        trackClick();
        if (!isEnrolled) {
            setIsExpanded((prev) => !prev);
        }
    }, [isEnrolled, trackClick]);

    const handleExpandButtonClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setIsExpanded((prev) => !prev);
    }, []);

    const CourseMedia = ({layout}: {layout: 'square' | 'wide'}) => {
        if (!thumbnailUrl) {
            return null;
        }

        const mediaNode = (
            <CardMediaImage
                layout={layout}
                src={thumbnailUrl}
                {...(layout === 'wide' ? {sizes: '96px'} : {})}
            />
        );

        return cta?.href ? (
            <a href={cta.href} onClick={handleMediaLinkClick}>
                {mediaNode}
            </a>
        ) : (
            mediaNode
        );
    };

    const ctaNode = (
        <div className={styles['cta-button']}>
            <LearningPathCourseCta
                status={status}
                href={cta?.href}
                certificateHref={cta?.certificateHref}
                certificateReady={cta?.certificateReady}
                progressPercent={normalizedProgressPercent}
                isFirstIncompleteCourse={isFirstIncompleteCourse}
            />
        </div>
    );

    const preEnrolledToggleNode = (
        <div className={styles.action}>
            <IconButton
                onClick={handleExpandButtonClick}
                aria-label={isExpanded ? gettext('Collapse') : gettext('Expand')}
                aria-expanded={isExpanded}
                udStyle="ghost"
                size="medium"
            >
                <Icon
                    glyph="expand"
                    className={classNames(styles['expand-icon'], {
                        [styles.rotated]: isExpanded,
                    })}
                    size="medium"
                    label={false}
                    color="info"
                />
            </IconButton>
        </div>
    );
    const secondaryContentNode = isEnrolled ? ctaNode : preEnrolledToggleNode;

    return (
        <div className={styles['learning-path-course']}>
            <TrackImpression trackFunc={trackImpression}>
                <div
                    className={styles['course-card-row']}
                    onClick={handleCardRowClick}
                    role="presentation"
                >
                    <CourseProductCard
                        xCenterPrimaryContentVertically={true}
                        className={classNames(styles['course-card'], {
                            [styles['course-card-expanded']]: isExpanded,
                        })}
                        showSecondaryContentBorder={false}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-expect-error `CourseProductCard` supports `progress` at runtime, but its typings omit it.
                        progress={
                            normalizedProgressPercent > 0 ? (
                                <CardProgress
                                    value={normalizedProgressPercent}
                                    label={gettext('Course progress')}
                                />
                            ) : null
                        }
                        layout="horizontal"
                        title={
                            <div className={styles['title-stack']}>
                                <CardTitle>{courseTitle}</CardTitle>
                                <div className={styles['course-tracker']}>{trackerText}</div>
                            </div>
                        }
                        mediaSquare={<CourseMedia layout="square" />}
                        mediaWide={<CourseMedia layout="wide" />}
                        tags={tagItems.map(({key, node}) => (
                            <Tag key={key}>{node}</Tag>
                        ))}
                        inlineSecondaryContent={secondaryContentNode}
                        secondaryContent={secondaryContentNode}
                    />
                </div>
            </TrackImpression>
            <hr className={styles.separator} />
            {!isEnrolled && isExpanded ? (
                <div className={styles['expanded-curriculum']}>
                    <div className={styles['expanded-content']}>
                        <div className={styles['expanded-text-content']}>
                            {details?.title ? (
                                <h4 className={styles['expanded-title']}>{details.title}</h4>
                            ) : null}
                            {details?.keyPoints?.length ? (
                                <ul className={styles['expanded-list']}>
                                    {details.keyPoints.map((item, idx) => (
                                        <li
                                            key={`${idx}-${item}`}
                                            className={styles['expanded-item']}
                                        >
                                            <Icon glyph="tick" size="xsmall" label={false} />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : null}
                        </div>
                        {cta?.href ? (
                            <Button
                                componentClass="a"
                                href={cta.href}
                                udStyle={isWiderThanMdMin ? 'secondary' : 'ghost'}
                                size="medium"
                                className={styles['all-course-details-button']}
                            >
                                {gettext('All course details')}
                                <Icon
                                    label={false}
                                    glyph="arrow-right"
                                    className="ud-icon-bi-directional"
                                />
                            </Button>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
